import moment from 'moment';
import async from 'async-es';
import store from 'src/services/store';
import { setProgress } from 'src/services/pipelines/actions';
import { fields, filter } from 'src/types';
import { TYPE } from 'src/types/pipeline';

export const NA = 'N/A';
export const BUCKET_SIZE = 5000;

export const checkBooleanCondition = (pipeField, row) => {
  const field = fields[pipeField.code];

  if (pipeField.clause === NA) {
    // for bool fields an empty string contains one of null|undefined\NA
    const comparison = row[field.name] == null || row[field.name] === NA;
    return pipeField.is === filter.IS.NOT ? !comparison : comparison;
  }

  if (pipeField.clause == null || pipeField.clause === '') {
    // undefined|null clause means `Any` so any row fits the condition
    return true;
  }

  return row[field.name] === pipeField.clause;
};

export const checkCondition = (pipeField, row) => {
  const field = fields[pipeField.code];

  let comparison = false;

  const isNA = () =>
    pipeField.type === filter.TYPE.NA
    || `${pipeField.clause}`.toUpperCase() === NA.toUpperCase();

  if (field.isBoolean) {
    comparison = checkBooleanCondition(pipeField, row);
  } else {
    if (isNA()) {
      // for other fields an empty string contains one of undefined|null|false|empty|NA
      comparison = !row[field.name] || row[field.name] === NA;
      return pipeField.is === filter.IS.NOT ? !comparison : comparison;
    }

    if (!pipeField.clause) {
      // undefined|null clause means `Any` so any row fits the condition
      return true;
    }

    if (!row[field.name]) {
      // undefined value, here and under, is not comparable so it can not be valid
      return comparison;
    }

    if (field.isDateTime) {
      switch (pipeField.type) {
        case filter.TYPE_DATE.EQUAL:
          comparison = moment(row[field.name]).isSame(pipeField.clause);
          break;
        case filter.TYPE_DATE.BEFORE:
          comparison = moment(row[field.name]).isBefore(pipeField.clause);
          break;
        case filter.TYPE_DATE.AFTER:
          comparison = moment(row[field.name]).isAfter(pipeField.clause);
          break;
        case filter.TYPE_DATE.EQUAL_BEFORE:
          comparison = moment(row[field.name]).isSameOrBefore(pipeField.clause);
          break;
        case filter.TYPE_DATE.EQUAL_AFTER:
        default:
          comparison = moment(row[field.name]).isSameOrAfter(pipeField.clause);
          break;
      }
    } else {
      // ToDo [DFA-54] implement other types
      switch (pipeField.type) {
        case filter.TYPE.EQUAL:
          comparison = row[field.name].toLowerCase() === pipeField.clause.toLowerCase();
          break;
        case filter.TYPE.STARTS_WITH:
          comparison = row[field.name].toLowerCase().startsWith(pipeField.clause.toLowerCase());
          break;
        case filter.TYPE.ENDS_WITH:
          comparison = row[field.name].toLowerCase().endsWith(pipeField.clause.toLowerCase());
          break;
        case filter.TYPE.CONTAINS:
        default:
          comparison = row[field.name].toLowerCase().indexOf(pipeField.clause.toLowerCase()) > -1;
          break;
      }
    }
  }

  return pipeField.is === filter.IS.NOT ? !comparison : comparison;
};

export default (pipe, rows, pipeTotalPercent) => new Promise((resolve) => {
  const fieldsCount = pipe.fields.length;
  const fieldTotalPercent = pipeTotalPercent / fieldsCount;
  const rowsCount = rows.length;
  const rowTotalPercent = fieldTotalPercent / rowsCount;
  let progress = store.getState().pipelines.current.progress.percent;

  if (fieldsCount < 1) {
    return resolve(rows);
  }

  const result = [];

  const q = async.queue((bucket, callback) => setTimeout(() => {
    bucket.rows.reduce((prev, row) => {
      let fits = false;

      for (let i = 0; i < pipe.fields.length; i++) {
        const pipeField = pipe.fields[i];

        let fieldFit = false;

        if (row.rows) {
          // grouped row
          for (let j = 0; j < row.rows.length; j++) {
            fieldFit = fieldFit || checkCondition(pipeField, row.rows[j]);
          }
        } else {
          // plain row
          fieldFit = checkCondition(pipeField, row);
        }

        if (i === 0) {
          fits = fieldFit;
        } else {
          switch (pipe.logic) {
            case filter.LOGIC.OR:
              fits = fits || fieldFit;
              break;
            case filter.LOGIC.AND:
            default:
              fits = fits && fieldFit;
              break;
          }
        }

        progress += rowTotalPercent;
      }

      if (fits) {
        result.push(row);
      }

      return result;
    }, result);

    callback(bucket.last);
  }, 0));

  const taskCb = (isLast) => {
    store.dispatch(setProgress(progress, TYPE.MATCH));

    if (isLast) {
      resolve(result);
    }
  };

  let lastIndex = 0;

  while (lastIndex < rows.length - 1) {
    const end = lastIndex + BUCKET_SIZE;

    q.push({ rows: rows.slice(lastIndex, end), last: end >= rows.length }, taskCb);

    lastIndex = end;
  }
});